import { CollegeEligibilityFramework } from '../../interfaces/EOAInterface';
import { CollegeEligibilityFrameworkWriteBody } from '../../services/eoaConfigServices';
import { decimalNumberError } from './fieldValidation';
import { ensureDraftId } from './persistNestedConfiguration';

export type EligibilitySubject = 'English' | 'Math' | 'Social Science' | 'Science' | 'Elective' | 'World Language';

export type EligibilityColumnId = 'diploma' | 'eoaa' | 'ihe-0' | 'ihe-1';

export interface EligibilityCell {
  credits: string;
  note: string;
  noteOpen: boolean;
}

export interface EoaaInfo2Form {
  iheNames: [string, string];
  cells: Record<EligibilityColumnId, Record<EligibilitySubject, EligibilityCell>>;
  frameworkIds: Record<EligibilityColumnId, string | null>;
}

const COLLEGE_ELIGIBILITY_SUBJECTS: EligibilitySubject[] = [
  'English',
  'Math',
  'Social Science',
  'Science',
  'Elective',
  'World Language',
];

const ELIGIBILITY_COLUMN_IDS: EligibilityColumnId[] = ['diploma', 'eoaa', 'ihe-0', 'ihe-1'];

const DIPLOMA_COLUMN_NAME = 'High school diploma';
const EOAA_COLUMN_NAME = 'EOAA college eligibility';

const emptyCell = (): EligibilityCell => ({ credits: '', note: '', noteOpen: false });

const emptySubjectCells = (): Record<EligibilitySubject, EligibilityCell> =>
  COLLEGE_ELIGIBILITY_SUBJECTS.reduce(
    (cells, subject) => {
      cells[subject] = emptyCell();
      return cells;
    },
    {} as Record<EligibilitySubject, EligibilityCell>
  );

const emptyFrameworkIds = (): Record<EligibilityColumnId, string | null> => ({
  diploma: null,
  eoaa: null,
  'ihe-0': null,
  'ihe-1': null,
});

const INITIAL_EOAA_INFO_2_FORM: EoaaInfo2Form = {
  iheNames: ['', ''],
  cells: {
    diploma: emptySubjectCells(),
    eoaa: emptySubjectCells(),
    'ihe-0': emptySubjectCells(),
    'ihe-1': emptySubjectCells(),
  },
  frameworkIds: emptyFrameworkIds(),
};

const columnHeader = (form: EoaaInfo2Form, columnId: EligibilityColumnId): string => {
  if (columnId === 'diploma') return DIPLOMA_COLUMN_NAME;
  if (columnId === 'eoaa') return EOAA_COLUMN_NAME;
  const index = columnId === 'ihe-0' ? 0 : 1;
  const name = form.iheNames[index].trim();
  return name || `School #${index + 1}`;
};

const setIheName = (form: EoaaInfo2Form, index: 0 | 1, name: string): EoaaInfo2Form => {
  const iheNames: [string, string] = [...form.iheNames];
  iheNames[index] = name;
  return { ...form, iheNames };
};

const updateCell = (
  form: EoaaInfo2Form,
  columnId: EligibilityColumnId,
  subject: EligibilitySubject,
  patch: Partial<EligibilityCell>
): EoaaInfo2Form => ({
  ...form,
  cells: {
    ...form.cells,
    [columnId]: {
      ...form.cells[columnId],
      [subject]: { ...form.cells[columnId][subject], ...patch },
    },
  },
});

const encodeRequirement = (cell: EligibilityCell): string => {
  const credits = decimalNumberError(cell.credits) === null ? cell.credits.trim() : '';
  const note = cell.note.trim();
  if (credits && note) return `${credits}\n${note}`;
  return credits || note;
};

const decodeRequirement = (value: string | undefined): EligibilityCell => {
  if (!value) return emptyCell();
  const newlineIndex = value.indexOf('\n');
  if (newlineIndex === -1) {
    const credits = decimalNumberError(value) === null ? value : '';
    const note = credits ? '' : value;
    return { credits, note, noteOpen: note.length > 0 };
  }
  return {
    credits: value.slice(0, newlineIndex),
    note: value.slice(newlineIndex + 1),
    noteOpen: value.slice(newlineIndex + 1).trim().length > 0,
  };
};

const requirementsToCells = (requirements: Record<string, string>[]): Record<EligibilitySubject, EligibilityCell> => {
  const merged = Object.assign({}, ...requirements) as Record<string, string>;
  return COLLEGE_ELIGIBILITY_SUBJECTS.reduce((cells, subject) => {
    cells[subject] = decodeRequirement(merged[subject]);
    return cells;
  }, emptySubjectCells());
};

const cellsToRequirementMap = (cells: Record<EligibilitySubject, EligibilityCell>): Record<string, string> =>
  COLLEGE_ELIGIBILITY_SUBJECTS.reduce<Record<string, string>>((requirements, subject) => {
    const encoded = encodeRequirement(cells[subject]);
    if (encoded) requirements[subject] = encoded;
    return requirements;
  }, {});

interface ColumnWriteMeta {
  collegeName: (form: EoaaInfo2Form) => string | null;
  is_main_requirement: boolean;
  is_common_local_university: boolean;
  display_order: number;
}

const COLUMN_WRITE_META: Record<EligibilityColumnId, ColumnWriteMeta> = {
  diploma: {
    collegeName: () => DIPLOMA_COLUMN_NAME,
    is_main_requirement: false,
    is_common_local_university: false,
    display_order: 0,
  },
  eoaa: {
    collegeName: () => EOAA_COLUMN_NAME,
    is_main_requirement: true,
    is_common_local_university: false,
    display_order: 1,
  },
  'ihe-0': {
    collegeName: (form) => form.iheNames[0].trim() || null,
    is_main_requirement: false,
    is_common_local_university: true,
    display_order: 2,
  },
  'ihe-1': {
    collegeName: (form) => form.iheNames[1].trim() || null,
    is_main_requirement: false,
    is_common_local_university: true,
    display_order: 3,
  },
};

const toFrameworkWriteBody = (
  form: EoaaInfo2Form,
  columnId: EligibilityColumnId
): CollegeEligibilityFrameworkWriteBody => {
  const meta = COLUMN_WRITE_META[columnId];
  return {
    college_name: meta.collegeName(form),
    requirements: cellsToRequirementMap(form.cells[columnId]),
    is_main_requirement: meta.is_main_requirement,
    is_common_local_university: meta.is_common_local_university,
    display_order: meta.display_order,
  };
};

const comparableWriteBody = (body: CollegeEligibilityFrameworkWriteBody): string => JSON.stringify(body);

const areCreditFieldsSaveable = (form: EoaaInfo2Form): boolean =>
  ELIGIBILITY_COLUMN_IDS.every((columnId) =>
    COLLEGE_ELIGIBILITY_SUBJECTS.every((subject) => decimalNumberError(form.cells[columnId][subject].credits) === null)
  );

const hasInvalidEoaaInfo2 = (form: EoaaInfo2Form): boolean => !areCreditFieldsSaveable(form);

const cellsHaveContent = (cells: Record<EligibilitySubject, EligibilityCell>): boolean =>
  COLLEGE_ELIGIBILITY_SUBJECTS.some((subject) => {
    const cell = cells[subject];
    return Boolean((decimalNumberError(cell.credits) === null && cell.credits.trim()) || cell.note.trim());
  });

const columnHasContent = (form: EoaaInfo2Form, columnId: EligibilityColumnId): boolean => {
  if (columnId === 'ihe-0') return Boolean(form.iheNames[0].trim()) || cellsHaveContent(form.cells['ihe-0']);
  if (columnId === 'ihe-1') return Boolean(form.iheNames[1].trim()) || cellsHaveContent(form.cells['ihe-1']);
  return cellsHaveContent(form.cells[columnId]);
};

const hasCollegeEligibilityContent = (form: EoaaInfo2Form): boolean =>
  ELIGIBILITY_COLUMN_IDS.some((columnId) => columnHasContent(form, columnId));

const extractFrameworkId = (data: unknown): string => {
  if (typeof data === 'string' && data.length > 0) return data;
  if (data && typeof data === 'object') {
    if ('data' in data) return extractFrameworkId((data as { data: unknown }).data);
    if ('id' in data && typeof data.id === 'string' && data.id.length > 0) return data.id;
  }
  throw new Error('College eligibility create response did not include an id');
};

const pickFramework = (
  frameworks: CollegeEligibilityFramework[],
  predicate: (framework: CollegeEligibilityFramework) => boolean
): CollegeEligibilityFramework | undefined =>
  [...frameworks].sort((left, right) => left.display_order - right.display_order).find(predicate);

const eoaaInfo2FromFrameworks = (frameworks: CollegeEligibilityFramework[]): EoaaInfo2Form => {
  const diploma = pickFramework(
    frameworks,
    (framework) => !framework.is_main_requirement && !framework.is_common_local_university
  );
  const eoaa = pickFramework(frameworks, (framework) => framework.is_main_requirement);
  const ihes = frameworks
    .filter((framework) => framework.is_common_local_university)
    .sort((left, right) => left.display_order - right.display_order);

  return {
    iheNames: [ihes[0]?.college_name ?? '', ihes[1]?.college_name ?? ''],
    cells: {
      diploma: requirementsToCells(diploma?.requirements ?? []),
      eoaa: requirementsToCells(eoaa?.requirements ?? []),
      'ihe-0': requirementsToCells(ihes[0]?.requirements ?? []),
      'ihe-1': requirementsToCells(ihes[1]?.requirements ?? []),
    },
    frameworkIds: {
      diploma: diploma?.id ?? null,
      eoaa: eoaa?.id ?? null,
      'ihe-0': ihes[0]?.id ?? null,
      'ihe-1': ihes[1]?.id ?? null,
    },
  };
};

const persistCollegeEligibility = async ({
  form,
  previousForm,
  eoaId,
  createCollegeEligibility,
  patchCollegeEligibility,
  createDraft,
}: {
  form: EoaaInfo2Form;
  previousForm: EoaaInfo2Form;
  eoaId: string | null;
  createCollegeEligibility: (eoaId: string, payload: CollegeEligibilityFrameworkWriteBody) => Promise<unknown>;
  patchCollegeEligibility: (
    eoaId: string,
    frameworkId: string,
    payload: CollegeEligibilityFrameworkWriteBody
  ) => Promise<unknown>;
  createDraft?: () => Promise<string | null>;
}): Promise<EoaaInfo2Form> => {
  if (!areCreditFieldsSaveable(form)) return form;
  const draftId = await ensureDraftId({
    eoaId,
    hasContent: hasCollegeEligibilityContent(form),
    createDraft,
  });
  if (!draftId) return form;

  const frameworkIds = { ...form.frameworkIds };

  for (const columnId of ELIGIBILITY_COLUMN_IDS) {
    const nextBody = toFrameworkWriteBody(form, columnId);
    const previousBody = toFrameworkWriteBody(previousForm, columnId);
    const frameworkId = frameworkIds[columnId];

    if (!frameworkId) {
      if (!columnHasContent(form, columnId)) continue;
      frameworkIds[columnId] = extractFrameworkId(await createCollegeEligibility(draftId, nextBody));
      continue;
    }

    if (comparableWriteBody(previousBody) === comparableWriteBody(nextBody)) continue;
    await patchCollegeEligibility(draftId, frameworkId, nextBody);
  }

  return { ...form, frameworkIds };
};

export {
  COLLEGE_ELIGIBILITY_SUBJECTS,
  ELIGIBILITY_COLUMN_IDS,
  INITIAL_EOAA_INFO_2_FORM,
  columnHeader,
  eoaaInfo2FromFrameworks,
  hasInvalidEoaaInfo2,
  persistCollegeEligibility,
  setIheName,
  updateCell,
};
